import { useCallback, useContext, useEffect, useMemo } from 'react'
import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import useTourV2SnapshotDetails from 'hooks/TourV2/useTourV2SnapshotDetails'
import { useAppDispatch } from 'hooks/reduxHooks'
import { formatTourV2SnapshotRoom } from 'lib/format/formatTourV2SnapshotRoom'
import TextButton from 'LuxKit/Button/TextButton'
import Group from 'components/utils/Group'
import CaptionTextBlock from 'LuxKit/TextBlocks/CaptionTextBlock'
import StatusLabel from 'LuxKit/Label/StatusLabel'
import LineCompassIcon from 'LuxKit/Icons/line/LineCompassIcon'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import LesaPurchaseSummaryContainer from './Common/LesaPurchaseSummaryContainer'
import LesaPurchaseSummaryHeader from './Common/LesaPurchaseSummaryHeader'
import LesaPurchaseSummarySeparator from './Common/LesaPurchaseSummarySeparator'
import LesaPurchaseSummarySkeleton from './Common/LesaPurchaseSummarySkeleton'
import LesaPurchaseSummaryItineraryDates from './Common/LesaPurchaseSummaryItineraryDates'
import BodyText from 'LuxKit/Typography/BodyText'
import { ITEM_STATUS_CANCELLED, ITEM_STATUS_COMPLETED, ITEM_STATUS_PENDING } from 'constants/cart'
import Action from '../../Chat/MessageMeta/Action'
import { filterActions } from '../../../utils/actionsUtils'
import TourV2FinePrintModal from 'components/Common/OrderView/OrderItemSummaries/TourV2ItemSummary/TourV2FinePrintModal'
import ModalContext from 'contexts/ModalContext'
import LesaPurchaseSummaryTitle from './Common/LesaPurchaseSummaryTitle'
import { addChatMessage } from 'actions/supportAssistant/supportAssistantActions'
import useTourV2Snapshot from 'hooks/TourV2/useTourV2Snapshot'
import productLabels from 'i18n/productLabels'

interface Props {
  order: App.Order
  isPastOrder: boolean
  hideSelect?: boolean
  actions?: Array<App.ChatMessageMetaContent>
  payload?: App.ChatMessageMetaPayload
  componentsMap: Map<string | undefined, App.ChatMessageMetaContent>
  canEdit: boolean
}

function LesaTourV2PurchaseSummary(props: Props) {
  const { order, isPastOrder, hideSelect, actions, payload, componentsMap, canEdit } = props
  const intl = useIntl()
  const showModal = useContext(ModalContext)
  const orderItem = order.tourItems[0]
  const dispatch = useAppDispatch()
  const [snapshot] = useTourV2Snapshot(orderItem?.reservationId, orderItem?.orderId)

  const {
    formattedStartDate,
    formattedEndDate,
    daysCount,
    countriesCount,
    placesCount,
  } = useTourV2SnapshotDetails(snapshot)

  const mergedStatus = useMemo((): { status: App.OrderItemStatus | undefined, description: string | undefined } => {
    if (!snapshot) return { status: undefined, description: undefined }

    const isCompleted = orderItem.status === ITEM_STATUS_COMPLETED && snapshot?.status === 'confirmed'

    if (isCompleted) {
      return {
        status: ITEM_STATUS_COMPLETED,
        description: intl.formatMessage({
          id: 'TUul19LEwT40plQslRTZ',
          defaultMessage: 'Booking confirmed',
          description: 'Status description for confirmed tour booking in the tours section',
        }),
      }
    }

    const isCancelled = orderItem.status === ITEM_STATUS_CANCELLED || snapshot?.status === 'cancelled'

    if (isCancelled) {
      return {
        status: ITEM_STATUS_CANCELLED,
        description: intl.formatMessage({
          id: '9XvaYC_PsQOSFuVg0WBM',
          defaultMessage: 'Booking cancelled',
          description: 'Status description for cancelled tour booking in the tours section',
        }),
      }
    }

    return {
      status: ITEM_STATUS_PENDING,
      description: intl.formatMessage({
        id: 'Z5C5_9Dn6bhcKn2A50jR',
        defaultMessage: 'Awaiting confirmation',
        description: 'Status description for pending tour booking on the tour page',
      }),
    }
  }, [orderItem, snapshot, intl])

  const openFinePrintModal = useCallback(() => {
    if (snapshot) {
      showModal(<TourV2FinePrintModal
        finePrint={snapshot.finePrint}
        subtitle={snapshot.seasonName}
      />)
    }
  }, [showModal, snapshot])

  useEffect(() => {
    if (componentsMap?.get('fine-print-modal')) {
      openFinePrintModal()
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [componentsMap])

  const handleSelectClick = useCallback(() => {
    dispatch(addChatMessage('', { action: 'selectOrder', payload: { id_orders: order.id, canEditOrder: canEdit } }))
  }, [dispatch, order.id, canEdit])

  if (!snapshot) {
    return <LesaPurchaseSummarySkeleton />
  }

  const orderActions = actions?.filter(action => filterActions(action, isPastOrder, ['listInclusions', 'changeRoom']))

  const isSameLocation = snapshot?.startLocation === snapshot?.endLocation
  let locationSummary: React.ReactNode
  if (isSameLocation) {
    locationSummary = (
      <FormattedMessage
        id="dbZDNuHaZmSxKgJK1P3x"
        defaultMessage="Starts and ends in <bold>{location}</bold>"
        description="Tour purchase summary when start and end locations are the same"
        values={{ bold: (chunks) => <strong>{chunks}</strong>, location: snapshot?.startLocation }}
      />
    )
  } else {
    locationSummary = (
      <FormattedMessage
        id="GU2T_DQwGvJ-4b-qhnZy"
        defaultMessage="Starts in <bold>{startLocation}</bold>. Ends in <bold>{endLocation}</bold>"
        description="Tour purchase summary when start and end locations differ"
        values={{ bold: (chunks) => <strong>{chunks}</strong>, startLocation: snapshot?.startLocation, endLocation: snapshot?.endLocation }}
      />
    )
  }

  return (
    <>
      {(!!componentsMap?.get('list-orders') || !!componentsMap?.get('selectOrder')) && (
        <LesaPurchaseSummaryContainer>
          <LesaPurchaseSummaryHeader order={order}>
            <BodyTextBlock lineClamp={2} variant="small" startIcon={<LineCompassIcon size="XS" />}>{intl.formatMessage(productLabels.tours)}
            </BodyTextBlock>
            {!isPastOrder && <StatusLabel variant="confirmation">{intl.formatMessage({
              id: 'xKxETzphOxiCKOIS_e8V',
              defaultMessage: 'Upcoming',
              description: 'Status label for upcoming tour purchase on the tour offer detail page',
            })}
            </StatusLabel>}
            {isPastOrder && <StatusLabel variant="pending">{intl.formatMessage({
              id: 'MT83KCFMgCuFMhzWsO_x',
              defaultMessage: 'Past',
              description: 'Status label for past tour purchase on the tour offer detail page',
            })}
            </StatusLabel>}
          </LesaPurchaseSummaryHeader>
          <Group direction="vertical" tabletDirection="horizontal" horizontalAlign="space-between" verticalAlign="end" gap={16}>
            <Group direction="vertical" gap={4}>
              <CaptionTextBlock
                variant="medium"
                colour="light-foreground-alt"
              >
                {locationSummary}
              </CaptionTextBlock>
              <LesaPurchaseSummaryTitle orderId={order.id}>
                { snapshot.seasonName }
              </LesaPurchaseSummaryTitle>
              <LesaPurchaseSummarySeparator>
                {!!daysCount && <BodyText as="span" variant="small" colour="light-foreground-alt">{daysCount}</BodyText>}
                {!!countriesCount && <BodyText as="span" variant="small" colour="light-foreground-alt">{countriesCount}</BodyText>}
                {!!placesCount && <BodyText as="span" variant="small" colour="light-foreground-alt">{placesCount}</BodyText>}
              </LesaPurchaseSummarySeparator>
              <LesaPurchaseSummaryItineraryDates
                status={mergedStatus.status}
                showReservationDates={!!formattedStartDate}
                startDate={formattedStartDate}
                endDate={formattedEndDate}
                bookingNumber={orderItem?.bookingId}
              />
              {snapshot?.rooms.map((room, index) => (
                <LesaPurchaseSummarySeparator key={index}>
                  <BodyText as="span" variant="small" colour="light-foreground-alt">{formatTourV2SnapshotRoom(room, intl)}</BodyText>
                  <BodyText as="span" variant="small" colour="light-foreground-alt">{room.roomType}</BodyText>
                </LesaPurchaseSummarySeparator>
              ))}
            </Group>
            {!hideSelect &&
              <TextButton onClick={handleSelectClick} fit="mobile-full-width" size="small" kind="secondary">
                {intl.formatMessage({
                  id: '6FPnesUXNEAFJBgY3ESi',
                  defaultMessage: 'Select',
                  description: 'Button to select a tour purchase on the tour offer detail page',
                })}
              </TextButton>}
          </Group>
        </LesaPurchaseSummaryContainer>
      )}
      {!!orderActions?.length &&
        <Group fullWidth wrap="wrap" horizontalAlign="start" direction="vertical" tabletDirection="horizontal" gap={8}>
          {orderActions.map((action) => (
            <Action key={action.action?.key} action={action} payload={payload} />
          ))}
        </Group>}
    </>
  )
}

export default LesaTourV2PurchaseSummary
